import { deleteTextCommand } from '@blocksuite/affine-inline-preset';
import {
  pasteMiddleware,
  replaceIdMiddleware,
  surfaceRefToEmbed,
  uploadMiddleware,
} from '@blocksuite/affine-shared/adapters';
import {
  clearAndSelectFirstModelCommand,
  deleteSelectedModelsCommand,
  getBlockIndexCommand,
  getBlockSelectionsCommand,
  getImageSelectionsCommand,
  getSelectedModelsCommand,
  getTextSelectionCommand,
  retainFirstModelCommand,
} from '@blocksuite/affine-shared/commands';
import { DisposableGroup } from '@blocksuite/global/disposable';
import type { UIEventHandler } from '@blocksuite/std';
import type { BlockSnapshot, Store } from '@blocksuite/store';
import {
  LocaleProvider,
  NotificationProvider,
  getBlocksuiteLocaleKey,
  translateBlocksuiteText,
} from '@blocksuite/affine-shared/services';

import { ReadOnlyClipboard } from './readonly-clipboard';

/**
 * PageClipboard is a class that provides a clipboard for the page root block.
 * It is supported to copy and paste models in the page root block.
 */
export class PageClipboard extends ReadOnlyClipboard {
  static override key = 'affine-page-clipboard';

  protected _init = () => {
    this._initAdapters();
    const paste = pasteMiddleware(this.std);
    // Use surfaceRefToEmbed middleware to convert surface-ref to embed-linked-doc
    // When pastina a surface-ref block to another doc
    const surfaceRefToEmbedMiddleware = surfaceRefToEmbed(this.std);
    const replaceId = replaceIdMiddleware(this.std.store.workspace.idGenerator);
    const upload = uploadMiddleware(this.std);
    this.std.clipboard.use(paste);
    this.std.clipboard.use(surfaceRefToEmbedMiddleware);
    this.std.clipboard.use(replaceId);
    this.std.clipboard.use(upload);
    this._disposables.add({
      dispose: () => {
        this.std.clipboard.unuse(paste);
        this.std.clipboard.unuse(surfaceRefToEmbedMiddleware);
        this.std.clipboard.unuse(replaceId);
        this.std.clipboard.unuse(upload);
      },
    });
  };

  onBlockSnapshotPaste = async (
    snapshot: BlockSnapshot,
    doc: Store,
    parent?: string,
    index?: number
  ) => {
    const block = await this.std.clipboard.pasteBlockSnapshot(
      snapshot,
      doc,
      parent,
      index
    );
    return block?.id ?? null;
  };

  private _notifyPlainTextPaste = (
    data: DataTransfer,
    parent?: string,
    index?: number
  ) => {
    const plainText = data.getData('text/plain');
    const html = data.getData('text/html');
    const hasAffineClipboardData = data.types.some(type =>
      type.toLowerCase().startsWith('application/x-affine-')
    );
    const notification = this.std.getOptional(NotificationProvider);

    // AFFiNE's own clipboard already has an explicit rich-content format. Only
    // offer this for external HTML content that also has a text representation.
    if (
      !notification ||
      !html ||
      !plainText ||
      hasAffineClipboardData ||
      data.types.includes('BLOCKSUITE/SNAPSHOT')
    ) {
      return;
    }

    const translate = (key: string, fallback: string) =>
      translateBlocksuiteText(
        this.std.getOptional(LocaleProvider),
        getBlocksuiteLocaleKey('clipboard', key),
        fallback
      );
    const abortController = new AbortController();
    const invalidate = () => abortController.abort();
    const undoManager = this.std.store.history.undoManager;
    undoManager.on('stack-item-added', invalidate);
    const cleanup = () => {
      undoManager.off('stack-item-added', invalidate);
    };

    notification.notify({
      title: translate('plain-text.title', 'Paste as plain text?'),
      message: translate(
        'plain-text.message',
        'The pasted content contains formatting. Convert it to plain text?'
      ),
      accent: 'info',
      duration: 10_000,
      abort: abortController.signal,
      onClose: cleanup,
      actions: [
        {
          key: 'paste-as-plain-text',
          label: translate('plain-text.action', 'Paste plain text'),
          onClick: () => {
            // The rich paste is the most recent transaction. Undo it, then
            // feed the text back through the normal clipboard adapters. This
            // preserves Markdown conversion (links, lists, formulas, etc.).
            abortController.abort();
            this.std.store.undo();
            const plainEvent = {
              clipboardData: {
                files: [] as unknown as FileList,
                getData: (type: string) =>
                  type === 'text/plain' ? plainText : '',
                types: ['text/plain'],
              },
            } as unknown as ClipboardEvent;
            this.std.clipboard
              .paste(plainEvent, this.std.store, parent, index)
              .catch(console.error);
          },
        },
      ],
    });
  };

  private _pastePage = (
    event: ClipboardEvent,
    parent?: string,
    index?: number
  ) => {
    const data = event.clipboardData;
    this.std.clipboard
      .paste(event, this.std.store, parent, index)
      .then(() => {
        if (data) {
          this._notifyPlainTextPaste(data, parent, index);
        }
      })
      .catch(console.error);
  };

  onPageCut: UIEventHandler = ctx => {
    const e = ctx.get('clipboardState').raw;
    e.preventDefault();

    this._copySelectedInPage(() => {
      this.std.command
        .chain()
        .try<{}>(cmd => [
          cmd.pipe(getTextSelectionCommand).pipe(deleteTextCommand),
          cmd.pipe(getSelectedModelsCommand).pipe(deleteSelectedModelsCommand),
        ])
        .run();
    }).run();
  };

  onPagePaste: UIEventHandler = ctx => {
    const e = ctx.get('clipboardState').raw;
    e.preventDefault();

    if (this.std.store.readonly) return;
    this.std.store.captureSync();
    let hasPasteTarget = false;
    this.std.command
      .chain()
      .try<{}>(cmd => [
        cmd.pipe(getTextSelectionCommand).pipe((ctx, next) => {
          const { currentTextSelection } = ctx;
          if (!currentTextSelection) {
            return;
          }
          const { from, to } = currentTextSelection;
          if (to && from.blockId !== to.blockId) {
            this.std.command.exec(deleteTextCommand, {
              currentTextSelection,
            });
          }
          return next();
        }),
        cmd
          .pipe(getSelectedModelsCommand)
          .pipe(clearAndSelectFirstModelCommand)
          .pipe(retainFirstModelCommand)
          .pipe(deleteSelectedModelsCommand),
      ])
      .try<{ currentSelectionPath: string }>(cmd => [
        cmd.pipe(getTextSelectionCommand).pipe((ctx, next) => {
          const textSelection = ctx.currentTextSelection;
          if (!textSelection) {
            return;
          }
          next({ currentSelectionPath: textSelection.from.blockId });
        }),
        cmd.pipe(getBlockSelectionsCommand).pipe((ctx, next) => {
          const currentBlockSelections = ctx.currentBlockSelections;
          if (!currentBlockSelections) {
            return;
          }
          const blockSelection = currentBlockSelections.at(-1);
          if (!blockSelection) {
            return;
          }
          next({ currentSelectionPath: blockSelection.blockId });
        }),
        cmd.pipe(getImageSelectionsCommand).pipe((ctx, next) => {
          const currentImageSelections = ctx.currentImageSelections;
          if (!currentImageSelections) {
            return;
          }
          const imageSelection = currentImageSelections.at(-1);
          if (!imageSelection) {
            return;
          }
          next({ currentSelectionPath: imageSelection.blockId });
        }),
      ])
      .pipe(getBlockIndexCommand)
      .pipe((ctx, next) => {
        if (!ctx.parentBlock) {
          return;
        }
        hasPasteTarget = true;
        this._pastePage(
          e,
          ctx.parentBlock.model.id,
          ctx.blockIndex !== undefined ? ctx.blockIndex + 1 : 1
        );

        return next();
      })
      .run();

    if (hasPasteTarget) return;

    // If no valid selection target exists (for example, stale block selection
    // right after cut), create/focus the default paragraph and paste after it.
    const firstParagraphId = document
      .querySelector('affine-page-root')
      ?.focusFirstParagraph?.()?.id;
    const parentModel = firstParagraphId
      ? this.std.store.getParent(firstParagraphId)
      : null;
    const paragraphIndex =
      firstParagraphId && parentModel
        ? parentModel.children.findIndex(child => child.id === firstParagraphId)
        : -1;
    const insertIndex = paragraphIndex >= 0 ? paragraphIndex + 1 : undefined;

    this._pastePage(e, parentModel?.id, insertIndex);
  };

  override mounted() {
    if (!navigator.clipboard) {
      console.error(
        'navigator.clipboard is not supported in current environment.'
      );
      return;
    }
    if (this._disposables.disposed) {
      this._disposables = new DisposableGroup();
    }
    this.std.event.add('copy', this.onPageCopy);
    this.std.event.add('paste', this.onPagePaste);
    this.std.event.add('cut', this.onPageCut);
    this._init();
  }
}
